/** Longest side of a captured frame kept in memory. Phone cameras go to
 *  4000+ px; anything beyond what the warp can output is wasted memory. */
const MAX_CAPTURE_SIZE = 4000;

/** Draw any image source onto a new canvas, shrinking it if it is huge. */
export function toCanvas(
  source: CanvasImageSource,
  width: number,
  height: number,
  maxSize = MAX_CAPTURE_SIZE,
): HTMLCanvasElement {
  const scale = Math.min(1, maxSize / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas is not available in this browser");
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
}

/** Decode a picked file (camera fallback or photo library) to a canvas.
 *  createImageBitmap applies the EXIF rotation, so sideways phone photos
 *  arrive upright. */
export async function fileToCanvas(file: Blob): Promise<HTMLCanvasElement> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    throw new Error(
      "This browser can't open that picture. Try a JPEG or PNG, or use the camera.",
    );
  }
  try {
    return toCanvas(bitmap, bitmap.width, bitmap.height);
  } finally {
    bitmap.close();
  }
}

export function imageDataToCanvas(img: ImageData): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;
  canvas.getContext("2d")!.putImageData(img, 0, 0);
  return canvas;
}

export function canvasToJpeg(canvas: HTMLCanvasElement, quality = 0.92): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Could not encode the image"))),
      "image/jpeg",
      quality,
    );
  });
}

/** Turn a JPEG a quarter turn clockwise. */
export async function rotateJpeg(blob: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.height;
  canvas.height = bitmap.width;
  const ctx = canvas.getContext("2d")!;
  ctx.translate(canvas.width, 0);
  ctx.rotate(Math.PI / 2);
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  return canvasToJpeg(canvas);
}

/** Let React paint (e.g. a "Straightening…" message) before blocking the
 *  main thread with pixel work. */
export function nextPaint(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));
}
